/* Lịch hẹn: loại lịch, khung giờ, tải chi nhánh, tách sắp tới / trước đó, thống kê đến hẹn */
import dayjs from 'dayjs';
import { HOUR, type LeadAppt, type LeadLocal } from './logic';
import { DEP_ST, checkQuote, money, type Quote } from './quote';

export type ApptType = 'Tư vấn lần đầu' | 'Tư vấn lại' | 'Làm dịch vụ' | 'Tái khám';
export const APPT_TYPES: ApptType[] = ['Tư vấn lần đầu', 'Tư vấn lại', 'Làm dịch vụ', 'Tái khám'];
export const CANCEL_REASONS = ['Khách bận đột xuất', 'Khách chưa sắp xếp được', 'Khách ở xa / thời tiết', 'Đặt nhầm', 'Khác'] as const;

/** Khung giờ nhận lịch (phút từ 0h): 08:00 → 20:30, mỗi 30 phút */
export const SLOT_MIN = 30;
export const SLOTS = Array.from({ length: 26 }, (_, i) => 8 * 60 + i * SLOT_MIN);
/** Số khách tối đa một chi nhánh nhận trong một khung 30 phút */
export const SLOT_CAP = 4;
/** Buổi trong ngày để nhóm khung giờ: [tên, từ phút, đến phút) */
export const PERIODS: [string, number, number][] = [['Sáng', 8 * 60, 12 * 60], ['Chiều', 12 * 60, 17 * 60], ['Tối', 17 * 60, 21 * 60]];
export const slotLabel =(m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
export const slotOf = (at: number) => { const d = dayjs(at); return d.hour() * 60 + Math.floor(d.minute() / SLOT_MIN) * SLOT_MIN; };

const LIVE = (a: LeadAppt) => a.status === 'booked' || a.status === 'confirmed';

/** Số lịch đã đặt theo khung giờ của một chi nhánh trong một ngày (mọi khách trên máy, bỏ lịch huỷ / dời) */
export function slotLoad(byId: Record<number, LeadLocal>, branch: string, day: number, skipId?: string): Record<number, number> {
  const d = dayjs(day);
  const load: Record<number, number> = {};
  Object.values(byId).forEach((l) => l.appts.forEach((a) => {
    if (a.id === skipId || a.branch !== branch || a.status === 'cancelled' || a.status === 'noshow' || !dayjs(a.at).isSame(d, 'day')) return;
    const s = slotOf(a.at);
    load[s] = (load[s] ?? 0) + 1;
  }));
  return load;
}

/** Sắp tới: lịch còn hiệu lực chưa quá 2 giờ (cùng ngưỡng việc tiếp theo), gần nhất trước; còn lại là lịch trước đó, mới nhất trước */
export function splitAppts(appts: LeadAppt[], now: number) {
  const up = appts.filter((a) => LIVE(a) && a.at >= now - 2 * HOUR).sort((a, b) => a.at - b.at);
  const past = appts.filter((a) => !up.includes(a)).sort((a, b) => b.at - a.at);
  return { up, past };
}

/** Lịch đã qua giờ mà chưa ai ghi khách đến / không đến */
export const overdue = (a: LeadAppt, now: number) => LIVE(a) && a.at < now - 2 * HOUR;

export function apptStats(appts: LeadAppt[]) {
  const came = appts.filter((a) => a.status === 'arrived' || a.status === 'done').length;
  const noshow = appts.filter((a) => a.status === 'noshow').length;
  const moved = appts.filter((a) => a.status === 'cancelled' && a.movedTo).length;
  const cancelled = appts.filter((a) => a.status === 'cancelled' && !a.movedTo).length;
  // Tỉ lệ đến chỉ tính lịch đã có kết quả
  const rate = came + noshow ? Math.round((came / (came + noshow)) * 100) : null;
  return { total: appts.length, came, noshow, moved, cancelled, rate };
}

/** Lịch còn hiệu lực trùng ngày với giờ đang chọn (để nhắc dời thay vì đặt thêm) */
export const sameDayLive = (appts: LeadAppt[], at: number, skipId?: string) =>
  appts.find((a) => a.id !== skipId && LIVE(a) && dayjs(a.at).isSame(at, 'day'));

/* ---------- Đồng bộ với màn Tạo lịch hẹn (/appointment/create) ---------- */

export interface ApptRef { id: number; name: string }
export interface ApptSvc { id: number; name: string; price: number; qty: number }
/** Thông tin khách gửi kèm lịch hẹn — cùng các ô bên trái của màn Tạo lịch hẹn */
export interface ApptCustomer {
  name: string;
  /** Họ tên thật: thuộc hồ sơ khách, API lịch hẹn không nhận */
  fullname: string;
  phone: string;
  email: string;
  gender: number | null;
  /** YYYY-MM-DD */
  dob: string | null;
  isForeign: boolean;
  province: string | null;
  ward: string | null;
  address: string;
}
export interface ApptInput {
  customerId: number;
  customer: ApptCustomer;
  branchId?: number;
  at: number | null;
  staff?: ApptRef;
  telesale?: ApptRef;
  branchStaff?: ApptRef;
  services: ApptSvc[];
  note: string;
}

/** Giới tính theo màn Tạo lịch hẹn */
export const GENDERS = [{ value: 0, label: 'Không xác định' }, { value: 1, label: 'Nam' }, { value: 2, label: 'Nữ' }, { value: 3, label: 'Khác' }];
/** Trạng thái lịch trên hệ thống (/appointments) */
export const SYS_ST: Record<number, { label: string; color: string }> = {
  1: { label: 'Đặt lịch mới', color: 'blue' },
  2: { label: 'Đã xác nhận', color: 'cyan' },
  3: { label: 'Chờ thực hiện', color: 'orange' },
  4: { label: 'Đã thực hiện', color: 'green' },
  0: { label: 'Huỷ lịch', color: 'red' },
};

export const svcTotal = (s: ApptSvc[]) => s.reduce((t, x) => t + x.price * x.qty, 0);

/* ---------- Báo giá → lịch hẹn ---------- */

/** Báo giá đặt lịch theo được, mới nhất trước: bỏ nháp / đã huỷ / đã thay thế */
export const bookableQuotes = (quotes: Quote[] = []) =>
  quotes.filter((q) => q.status !== 'draft' && q.status !== 'cancelled' && q.status !== 'replaced').sort((a, b) => b.at - a.at);

/** Dịch vụ của báo giá → dịch vụ lịch hẹn (giá danh mục). Dịch vụ chưa có trong danh mục mang id âm: hiện nhưng không gửi lên hệ thống */
export const quoteSvcs = (q: Quote): ApptSvc[] => q.items.reduce<ApptSvc[]>((xs, x, i) => {
  const id = x.id ?? -(9000 + i);
  const hit = xs.find((s) => s.id === id);
  return hit ? xs.map((s) => (s === hit ? { ...s, qty: s.qty + 1 } : s)) : [...xs, { id, name: x.name, price: x.price ?? 0, qty: 1 }];
}, []);

/** Dòng ghi chú lịch hẹn tóm tắt báo giá: mã, giá sau giảm, ưu đãi tặng kèm, cọc */
export const quoteNote = (q: Quote) => {
  const c = checkQuote(q.items, q.discount, q.reason);
  return [
    `Theo báo giá ${q.code}`,
    q.discount ? `giảm ${q.discount}% còn từ ${money(c.after)}` : `từ ${money(c.total)}`,
    q.promo && `tặng ${q.promo}`,
    q.deposit && `cọc ${q.deposit.code} ${money(q.deposit.amount)} (${DEP_ST[q.deposit.status].label})`,
  ].filter(Boolean).join(' · ');
};

/** Thay dòng báo giá cũ trong ghi chú bằng dòng mới (line rỗng = bỏ), giữ phần người dùng tự ghi */
export const withQuoteNote = (note: string, line: string) =>
  [line, ...note.split('\n').filter((x) => !/^Theo báo giá BG-/.test(x))].filter((x) => x.trim()).join('\n');

/** Kiểm tra giống màn Tạo lịch hẹn + giờ nhận lịch của cơ sở */
export function apptInputError(f: ApptInput, now: number): string | null {
  const c = f.customer;
  if (!c.name.trim()) return 'Họ và tên là bắt buộc.';
  const miss = [
    !c.phone.trim() && 'Số điện thoại',
    !c.isForeign && !c.province && 'Tỉnh/Thành phố',
    !c.isForeign && !c.ward && 'Phường/Xã',
    !c.address.trim() && 'Địa chỉ',
  ].filter(Boolean);
  if (miss.length) return `Vui lòng cập nhật ${miss.join(', ')} của khách hàng để tạo lịch hẹn.`;
  if (!f.branchId) return 'Vui lòng chọn chi nhánh.';
  if (!f.at) return 'Chọn thời gian hẹn.';
  if (f.at <= now) return 'Giờ hẹn đã qua.';
  const h = dayjs(f.at).hour();
  if (h < 8 || h >= 21) return 'Cơ sở chỉ nhận lịch từ 08:00 đến 21:00.';
  return null;
}

/** Payload đúng như màn Tạo lịch hẹn gửi POST /api/v1/be/appointments — để nối API sau */
export function toApiPayload(f: ApptInput): Record<string, unknown> {
  const c = f.customer;
  // Chi nhánh / dịch vụ mẫu (id âm, khi chưa tải được danh mục) không gửi lên
  const p: Record<string, unknown> = { customer_name: c.name.trim(), status: 1, is_foreign: c.isForeign ? 1 : 0 };
  if (f.branchId && f.branchId > 0) p.branch_id = f.branchId;
  if (c.phone) p.customer_phone = c.phone.trim();
  if (c.email) p.customer_email = c.email.trim();
  if (c.gender) p.customer_gender = c.gender;
  if (c.dob) p.customer_dob = c.dob;
  p.customer_id = f.customerId;
  if (c.province) p.province_code = c.province;
  if (c.ward) p.ward_code = c.ward;
  if (c.address) p.address = c.address.trim();
  if (f.at) p.scheduled_at = dayjs(f.at).format('YYYY-MM-DD HH:mm:ss');
  const svc = f.services.filter((s) => s.id > 0);
  if (svc.length) p.service_ids = svc.map((s) => s.id);
  if (f.note.trim()) p.note = f.note.trim();
  if (f.branchStaff) p.branch_staff_id = f.branchStaff.id;
  if (f.telesale) p.telesale_id = f.telesale.id;
  if (f.staff) p.staff_id = f.staff.id;
  return p;
}

/** Loại lịch gợi ý: chưa từng đến → tư vấn lần đầu; đã chốt / cọc → làm dịch vụ; còn lại → tư vấn lại */
export function suggestType(appts: LeadAppt[]): ApptType {
  const visits = appts.filter((a) => a.status === 'arrived' || a.status === 'done');
  if (!visits.length) return 'Tư vấn lần đầu';
  if (visits.some((a) => a.outcome?.kind === 'won' || a.outcome?.kind === 'deposit')) return 'Làm dịch vụ';
  return 'Tư vấn lại';
}
